import React from 'react'
import Table from "react-bootstrap/Table"

interface DataTableProps<T = Record<string, unknown>> {
    columns: ColumnDefinition<T>[]
    rows: T[]
    summaryRows?: Array<Array<CellDefinition | React.ReactNode>>
    onRowClick?: (index: number, obj: T) => void
    checkboxHeader?: React.ReactNode
    sort?: string | null
    onSortChange?: (newSort: string) => void
}

function DataTable<T = Record<string, unknown>>({ columns, rows, summaryRows, onRowClick, checkboxHeader, sort, onSortChange }: DataTableProps<T>) {
    const body = rows.map((obj, i1) => {
        const cells = columns.map((column, i2) =>
            <td key={i2} className={column.classNames ?? undefined}>{column.extractValue(obj, i1)}</td>
        )
        return <tr key={i1} onClick={() => onRowClick && onRowClick(i1, obj)} style={onRowClick ? { cursor: 'pointer' } : undefined}>{cells}</tr>
    })

    const head = columns.map((column, index) => {
        const isSortable = column.sortKey != null && onSortChange != null
        let indicator: React.ReactNode = null
        let isActive = false
        let nextSort: string | null = null
        if (isSortable) {
            const currentKey = sort?.startsWith("-") ? sort.slice(1) : sort
            const isDesc = sort != null && sort.startsWith("-")
            isActive = currentKey === column.sortKey
            indicator = isActive ? (isDesc ? " ▼" : " ▲") : " ↕"
            if (isActive) {
                nextSort = isDesc ? column.sortKey! : `-${column.sortKey}`
            } else {
                nextSort = column.sortKey!
            }
        }
        const headerContent = isSortable
            ? <a href="#" onClick={(e) => { e.preventDefault(); onSortChange!(nextSort!) }} style={{ color: isActive ? 'inherit' : undefined, textDecoration: isActive ? 'underline' : 'none' }}>{column.label}{indicator}</a>
            : column.label
        return <th key={index} className={column.headerClassNames ?? undefined}>{index === 0 && checkboxHeader ? checkboxHeader : headerContent}</th>
    })

    const summary = summaryRows && summaryRows.map((row, i) =>
        <tr className="summary-row" key={`summary-${i}`}>
            {row.map((cell, j) => {
                const value = cell instanceof CellDefinition ? cell.value : cell
                const classNames = cell instanceof CellDefinition ? cell.classNames : null
                return <td key={j} className={classNames ?? undefined}>{value as React.ReactNode}</td>
            })}
        </tr>
    )

    return (
        <Table striped hover size="sm">
            <thead><tr>{head}</tr></thead>
            <tbody>{summary}{body}</tbody>
        </Table>
    )
}

class ColumnDefinition<T = Record<string, unknown>> {
    label: string
    valueExtraction: string | ((obj: T, index: number) => React.ReactNode) | null
    classNames: string | null
    headerClassNames: string | null
    sortKey: string | null

    constructor(
        label: string,
        valueExtraction: string | ((obj: T, index: number) => React.ReactNode) | null = null,
        classNames: string | null = null,
        headerClassNames: string | null = null,
        sortKey: string | null = null,
    ) {
        this.label = label
        this.valueExtraction = valueExtraction
        this.classNames = classNames
        this.headerClassNames = headerClassNames
        this.sortKey = sortKey
    }

    extractValue(object: T, index: number): React.ReactNode {
        if (this.valueExtraction == null) {
            return (object as Record<string, unknown>)[this.label] as React.ReactNode
        } else if (typeof this.valueExtraction === 'string' || this.valueExtraction instanceof String) {
            return (object as Record<string, unknown>)[this.valueExtraction as string] as React.ReactNode
        } else {
            return this.valueExtraction(object, index)
        }
    }
}

class CellDefinition {
    value: React.ReactNode
    classNames: string | null

    constructor(value: React.ReactNode, classNames: string | null = null) {
        this.value = value
        this.classNames = classNames
    }
}

export { DataTable, ColumnDefinition, CellDefinition }